import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';

import EmailReVerificationModal from '../components/EmailReVerificationModal';
import { queryKeys } from '../services/react-query/queryKeys';
import { EMAIL_VERIFICATION_PERIOD_DAYS } from '../utils/constants/emailVerification';

import { PermissionsContext } from './permissionsContext';

interface EmailVerificationContextValue {
  showVerificationModal: boolean;
  triggerVerification: () => void;
  dismissVerification: () => void;
  isVerificationRequired: boolean;
  userEmail: string;
  resentCount: number;
  handleVerificationSuccess: () => void;
}

const EmailVerificationContext = createContext<EmailVerificationContextValue>({
  showVerificationModal: false,
  triggerVerification: () => undefined,
  dismissVerification: () => undefined,
  isVerificationRequired: false,
  userEmail: '',
  resentCount: 0,
  handleVerificationSuccess: () => undefined,
});

interface EmailVerificationProviderProps {
  children: ReactNode;
  verificationPeriodDays?: number;
}

export const EmailVerificationProvider: React.FC<EmailVerificationProviderProps> = ({
  children,
  verificationPeriodDays = EMAIL_VERIFICATION_PERIOD_DAYS,
}) => {
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resentCount, setResentCount] = useState(0);
  const [isVerificationRequired, setIsVerificationRequired] = useState(false);
  const [verificationJustCompleted, setVerificationJustCompleted] = useState(false);

  const { loading, userData } = useContext(PermissionsContext);
  const queryClient = useQueryClient();

  const checkVerificationStatus = useCallback(() => {
    if (loading || !userData || verificationJustCompleted) {
      return false;
    }

    setUserEmail((userData as { email?: string })?.email || '');
    setResentCount(userData.resent_count || 0);

    if (!userData.last_verified_at) return false;

    const lastVerifiedDate = new Date(userData.last_verified_at + 'T00:00:00.000Z');
    const currentDate = new Date();
    const timeDiff = currentDate.getTime() - lastVerifiedDate.getTime();
    const daysSinceVerification = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    return daysSinceVerification > verificationPeriodDays;
  }, [loading, userData, verificationJustCompleted, verificationPeriodDays]);

  useEffect(() => {
    if (verificationJustCompleted || loading || !userData) return;

    const needsVerification = checkVerificationStatus();
    setIsVerificationRequired(needsVerification || false);

    if (needsVerification && !showVerificationModal) {
      setShowVerificationModal(true);
    }
  }, [
    loading,
    userData,
    userData?.last_verified_at,
    verificationJustCompleted,
    showVerificationModal,
    checkVerificationStatus,
  ]);

  const triggerVerification = useCallback(() => {
    setShowVerificationModal(true);
  }, []);

  const dismissVerification = () => {
    setShowVerificationModal(false);
    setIsVerificationRequired(false);
  };

  const handleVerificationSuccess = async () => {
    setVerificationJustCompleted(true);
    setShowVerificationModal(false);
    setIsVerificationRequired(false);
    await queryClient.invalidateQueries({ queryKey: queryKeys.auth.userInfo() });
  };

  const contextValue: EmailVerificationContextValue = {
    showVerificationModal,
    triggerVerification,
    dismissVerification,
    isVerificationRequired,
    userEmail,
    resentCount,
    handleVerificationSuccess,
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (
        window as typeof window & { triggerEmailVerification?: () => void }
      ).triggerEmailVerification = triggerVerification;
    }
  }, [triggerVerification]);

  return (
    <EmailVerificationContext.Provider value={contextValue}>
      {showVerificationModal ? (
        <EmailReVerificationModal
          open={showVerificationModal}
          onSuccess={handleVerificationSuccess}
          userEmail={userEmail}
          resentCount={resentCount}
          setShowModal={setShowVerificationModal}
        />
      ) : (
        children
      )}
    </EmailVerificationContext.Provider>
  );
};

export const useEmailVerification = (): EmailVerificationContextValue => {
  const context = useContext(EmailVerificationContext);
  if (!context) {
    throw new Error('useEmailVerification must be used within EmailVerificationProvider');
  }
  return context;
};
